import { Point as FabricPoint, util } from 'fabric';
import React from 'react';

import { Coords } from 'editor/src/store/design/types';

import useFabricCanvas from 'editor/src/util/useFabricCanvas';
import useFabricUtils from 'editor/src/util/useFabricUtils';

import { CanvasRotation } from '../types';
import useForceUpdateOnViewportChange from '../useForceUpdateOnViewportChange';

import useRulerGuides from './context';
import { documentGuideAxis, GuideAxis, guideSegment, RulerGuide } from './geometry';

interface Props {
  coords: Coords;
  width: number;
  height: number;
  rotation: CanvasRotation;
  draft?: RulerGuide;
  onPointerDown: (event: React.PointerEvent, axis: GuideAxis, guide: RulerGuide) => void;
}

export default function RulerGuides({ coords, width, height, rotation, draft, onPointerDown }: Props) {
  const canvas = useFabricCanvas();
  const { mm2px } = useFabricUtils();
  const { guides } = useRulerGuides();
  useForceUpdateOnViewportChange();
  // Moving the captured SVG node in the DOM interrupts the browser's pointer capture.
  const visible = guides.map((guide) => (guide.id === draft?.id ? draft : guide));
  if (draft && !guides.some((guide) => guide.id === draft.id)) {
    visible.push(draft);
  }

  return (
    <svg style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', pointerEvents: 'none' }}>
      {visible.map((guide) => {
        const [from, to] = guideSegment(guide, coords, width, height, mm2px, rotation).map((point) =>
          util.transformPoint(new FabricPoint(point), canvas.viewportTransform),
        );
        const vertical = documentGuideAxis(guide.axis, rotation.angleDeg) === 'vertical';
        const cursor = vertical ? 'ew-resize' : 'ns-resize';
        const labelPosition = vertical
          ? Math.max(56, Math.min(canvas.getWidth() - 36, from.x))
          : Math.max(56, Math.min(canvas.getHeight() - 36, from.y));
        return (
          <g key={guide.id} data-guide-axis={guide.axis} data-guide-position={guide.position}>
            <line x1={from.x} y1={from.y} x2={to.x} y2={to.y} stroke="#E500E5" strokeWidth={1} />
            <line
              x1={from.x}
              y1={from.y}
              x2={to.x}
              y2={to.y}
              stroke="transparent"
              strokeWidth={8}
              style={{
                pointerEvents: 'stroke',
                cursor,
              }}
              onPointerDown={(event) => onPointerDown(event, guide.axis, guide)}
            />
            <g
              data-guide-label={guide.id}
              transform={vertical ? `translate(${labelPosition} 10)` : `translate(10 ${labelPosition}) rotate(-90)`}
              style={{ pointerEvents: 'auto', cursor }}
              onPointerDown={(event) => onPointerDown(event, guide.axis, guide)}
            >
              <rect x={-35} y={-9} width={70} height={18} rx={3} fill="white" stroke="#A100A1" />
              <text textAnchor="middle" dominantBaseline="central" fontSize={11} fill="#750075">
                {Number(guide.position.toFixed(2))} mm
              </text>
            </g>
          </g>
        );
      })}
    </svg>
  );
}
